import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { fetchUserProfile, refreshToken, setRoleInfo } from 'utils/auth'; // Import refreshToken
import { DEFAULT_PROFILE } from 'constants/authConstants'; // Import the constants
import { useNavigate } from 'react-router-dom'; // Import useNavigate from react-router-dom
import {
  getAwardRoles,
  setUserEmail,
  getUserCategories,
  getUserPillars,
  getUserRoles,
} from 'services/awardRoles';

interface Profile {
  fullName: string;
  email: string;
}

interface AwardRoleData {
  documentId?: string;
  email: string;
  role_type: string;
  pillar: string;
  category: string;
}

interface AwardRoles {
  data: AwardRoleData[];
  meta: {
    count: number;
  };
}

interface User {
  accessToken: string;
  profile?: Profile;
  awardRoles?: AwardRoles | null;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  awardRoles: AwardRoles | null;
  userCategories: string[];
  userPillars: string[];
  userRoles: string[];
  login: (accessToken: string, code: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  awardRoles: null,
  userCategories: [],
  userPillars: [],
  userRoles: [],
  login: async () => {},
  logout: () => {},
  isAuthenticated: () => false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
    // Don't auto-create dummy user on localhost - only create on explicit login
    return null;
  });

  const [profile, setProfile] = useState<Profile | null>(() => {
    const initialUser = JSON.parse(localStorage.getItem('user') || 'null');
    return initialUser?.profile || DEFAULT_PROFILE;
  });

  const [awardRoles, setAwardRoles] = useState<AwardRoles | null>(() => {
    const initialUser = JSON.parse(localStorage.getItem('user') || 'null');
    return initialUser?.awardRoles || null;
  });

  const [userCategories, setUserCategories] = useState<string[]>([]);
  const [userPillars, setUserPillars] = useState<string[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);

  const navigate = useNavigate(); // Initialize useNavigate
  const isLoggingOut = useRef(false); // Track logout state

  const fetchAndSetAwardRoles = useCallback(async email => {
    // Don't fetch roles if we're in the process of logging out
    if (isLoggingOut.current) {
      return;
    }

    try {
      // Set user email in localStorage for the award roles service
      setUserEmail(email);

      // Fetch award roles using the new service
      const awardRolesData = await getAwardRoles();

      if (awardRolesData.data && awardRolesData.data.length > 0) {
        // Extract the first role data (assuming one role per user for now)
        const roleData = awardRolesData.data[0];

        // Store the specific role data in localStorage
        const roleInfo = {
          email: roleData.email,
          role_type: roleData.role_type,
          pillar: roleData.pillar,
          category: roleData.category,
        };

        setRoleInfo(roleInfo);

        setAwardRoles(awardRolesData);

        // Extract categories, pillars, and roles
        const categories = await getUserCategories();
        const pillars = await getUserPillars();
        const roles = await getUserRoles();

        setUserCategories(categories as string[]);
        setUserPillars(pillars as string[]);
        setUserRoles(roles as string[]);

        // Update stored user data
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
          storedUser.awardRoles = awardRolesData;
          localStorage.setItem('user', JSON.stringify(storedUser));
        }
      } else {
        // No roles found, set empty data
        setAwardRoles({ data: [], meta: { count: 0 } });
        setUserCategories([]);
        setUserPillars([]);
        setUserRoles([]);

        // Clear role info from localStorage
        localStorage.removeItem('awardRoleInfo');
      }
    } catch {
      // console.error('Failed to fetch award roles:', error);
      // Set empty data on error
      setAwardRoles({ data: [], meta: { count: 0 } });
      setUserCategories([]);
      setUserPillars([]);
      setUserRoles([]);

      // Clear role info from localStorage on error
      localStorage.removeItem('awardRoleInfo');
    }
  }, []);

  const fetchAndSetUserProfile = useCallback(
    async accessToken => {
      // Don't fetch profile if we're in the process of logging out
      if (isLoggingOut.current) {
        return;
      }

      try {
        const profileData = await fetchUserProfile(accessToken);

        // Add null checks to prevent errors
        let email = '';
        if (profileData && profileData.DN) {
          email = profileData.DN.split(',')[0].split('=')[1];
        } else if (profileData && profileData.email) {
          email = profileData.email;
        }

        const newProfile = {
          fullName: profileData?.FullName || 'Unknown User',
          email,
        };

        setProfile(newProfile);

        if (email) {
          // Fetch award roles after successful profile fetch
          await fetchAndSetAwardRoles(email);
        }
      } catch (error) {
        if (error.status === 403) {
          logout(); // Trigger logout if 403 error occurs
        } else {
          // console.error('Failed to fetch user profile:', error);
          setProfile(DEFAULT_PROFILE); // Use the constant
        }
      }
    },
    [fetchAndSetAwardRoles]
  );

  useEffect(() => {
    // Don't run useEffect if we're logging out
    if (isLoggingOut.current) {
      return;
    }

    const isLocalhost = window.location.hostname === 'localhost';

    if (!isLocalhost) {
      // For non-localhost environments, use the existing logic.
      const storedUser = localStorage.getItem('user');
      const storedCode = localStorage.getItem('oauth_code'); // Get stored code
      if (storedUser && storedCode) {
        const parsedUser = JSON.parse(storedUser);
        const refreshAndSetUser = async () => {
          try {
            const newAccessToken = await refreshToken(storedCode);
            parsedUser.accessToken = newAccessToken;
            setUser(parsedUser);
            localStorage.setItem('user', JSON.stringify(parsedUser));
            fetchAndSetUserProfile(newAccessToken);
          } catch {
            // console.error('Failed to refresh token:', error);
            logout();
          }
        };
        refreshAndSetUser();
      }
    } else {
      // For localhost, only fetch roles if user already exists (don't auto-create)
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.profile?.email) {
          fetchAndSetAwardRoles(parsedUser.profile.email);
        }
      }
    }
  }, [fetchAndSetUserProfile, fetchAndSetAwardRoles]);

  const login = async (accessToken, code) => {
    // Reset logout flag
    isLoggingOut.current = false;

    if (window.location.hostname === 'localhost') {
      // LOCALHOST DEVELOPMENT: Create a dummy user to bypass OAuth authentication
      // This allows developers to test the application locally without setting up OAuth
      // Note: While authentication is bypassed, authorization (RBAC) is still enforced
      // using real role data from the Strapi database based on the email below
      const dummyUser = {
        accessToken: 'dummy-token-for-localhost', // Fake token for localhost
        profile: {
          ...DEFAULT_PROFILE,
          fullName: 'Raosan', // Current development user
          email: 'raosan@hyperjump.tech', // This email will be used to fetch real roles from Strapi
          // fullName: 'Puja Romulus', // Alternative development user name
          // email: 'puja.romulus@srx.id', // Alternative development user email
        },
        awardRoles: null, // Will be populated by fetchAndSetAwardRoles
      };
      setUser(dummyUser);
      setProfile(dummyUser.profile);
      setAwardRoles(dummyUser.awardRoles);
      localStorage.setItem('user', JSON.stringify(dummyUser));

      // Fetch real award roles from Strapi database using the dummy email
      // This ensures RBAC (Role-Based Access Control) works properly in development
      await fetchAndSetAwardRoles(dummyUser.profile.email);
      return;
    }

    const userData = { accessToken };
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('oauth_code', code); // Store the code

    // Fetch user profile and award roles after login
    await fetchAndSetUserProfile(accessToken);
  };

  const logout = () => {
    // console.log('Logout initiated - clearing all auth states...');

    // Set logout flag to prevent auto-refetching
    isLoggingOut.current = true;

    // STEP 1: Clear all localStorage first (to prevent side effects)
    // console.log('Step 1: Clearing localStorage...');
    localStorage.removeItem('user');
    localStorage.removeItem('oauth_code');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('awardRoleInfo');

    // STEP 2: Clear React state immediately
    // console.log('Step 2: Clearing React state...');
    setUser(null);
    setProfile(DEFAULT_PROFILE);
    setAwardRoles(null);
    setUserCategories([]);
    setUserPillars([]);
    setUserRoles([]);

    // STEP 3: Navigate to home page
    // console.log('Step 3: Redirecting to home...');
    navigate('/');

    // Reset logout flag after navigation
    setTimeout(() => {
      isLoggingOut.current = false;
      // console.log('Logout completed');
    }, 500);
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        awardRoles,
        userCategories,
        userPillars,
        userRoles,
        login,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
